import type { ErrorEvent } from '@sentry/nextjs';
import {
  getClient,
  getCurrentScope,
  getGlobalScope,
  getIsolationScope,
  init,
} from '@sentry/nextjs';

import { redactBreadcrumbQuery } from './breadcrumb-query-redaction';
import type {
  DropReason,
  IgnoredDropHandler,
  IgnoredDropInfo,
} from './drop-telemetry';
import {
  DROP_REASONS,
  resolveEventText,
  resolveRequestHostname,
} from './drop-telemetry';
import { isPreviewEnvironment } from './preview-environment';
import type {
  ProcessorDropHandler,
  SinkArmedHandler,
} from './processor-drop-telemetry';
import { instrumentEventProcessorDrops } from './processor-drop-telemetry';
import {
  createIgnoredDropReporter,
  DROP_REPORT_WINDOW_MS,
} from './server-drop-reporter';
import {
  isNextNotFoundError,
  resolveEnvironment,
} from './server-event-predicates';
import {
  isMissingSupabaseKeysError,
  isPreviewSupabaseKeysTypeError,
} from './supabase-key-errors';

type Parameters<T extends (args: never) => unknown> = T extends (
  ...args: infer P
) => unknown
  ? P
  : never;

/**
 * Messages that MUST reach Sentry no matter what `IGNORED_ERROR_PATTERNS`
 * says. This list has precedence over every ignore pattern below.
 *
 * Why a precedence list and not just "be careful what you add": the failure
 * mode here has now happened twice. A broad benign-noise pattern is added for
 * one reason, and later a new outage signal starts arriving in a message that
 * the old pattern happens to match. Nobody notices, because a drop leaves no
 * trace. Reviewing each new pattern against seven forbidden strings is a
 * process control; this is a mechanical one.
 *
 * The first seven entries are the outage-symptom strings the
 * `critical-rules-supabase-outage-resilience` clause forbids (Layer 5). The
 * abort/timeout entries close the BAL-891 gap.
 */
const OUTAGE_SIGNAL_PATTERNS = [
  /failed to fetch/i,
  /connection closed/i,
  /terminated/i,
  /connection pool exhausted/i,
  /remaining connection slots/i,
  /database service temporarily unavailable/i,
  /too many connections/i,
  // Layer 6 (`makeTimeoutFetch`) bounds every server-side Supabase call with
  // `AbortSignal.timeout(ms)`. When that budget blows, the rejection is a
  // DOMException named `TimeoutError` with the message "The operation was
  // aborted due to timeout" (verified on Node 26), which postgrest-js then
  // stamps as `TimeoutError: The operation was aborted due to timeout`
  // (PostgrestBuilder.ts:446, `${name}: ${message}`).
  //
  // That is our own bounded-fetch guarantee firing — the primary signal
  // during a Postgres slowdown — and until BAL-891 the bare `/abort/i`
  // pattern silently nulled every one of them. 551 events vanished in three
  // hours on 2026-08-09/10 with zero trace. See BAL-891.
  //
  // BAL-972 had to make postgrest-js stop retrying that same rejection, and
  // postgrest's guard reads `name === 'AbortError' || code === 'ABORT_ERR'`.
  // `packages/supabase/src/lib/timeout-fetch.ts` therefore marks `code` and
  // deliberately does NOT rename to `AbortError` — a rename satisfies the
  // guard just as well and re-silences this signal here, because
  // `IGNORED_ERROR_PATTERNS` below drops `AbortError` by design. If you ever
  // change how Layer 6 shapes that error, the two patterns immediately below
  // are what stop working.
  /aborted due to timeout/i,
  /timeouterror/i,
];

function isOutageSignal(errorValue: string): boolean {
  return OUTAGE_SIGNAL_PATTERNS.some((pattern) => pattern.test(errorValue));
}

// Error patterns to completely ignore (transient/expected errors)
//
// IMPORTANT: do NOT add patterns matching Postgres-outage symptoms here.
// Outage symptom classes (forbidden in this list) include: upstream fetch
// failures, dropped/reset/closed connections, pool-saturation messages,
// reserved-slot warnings, Postgres temporarily-unavailable signals,
// excessive-connection errors, and our own Layer-6 fetch-budget timeouts.
// These are the only signals we get during a Supabase Postgres outage
// (Cloudflare 522, pool exhaustion, REST 5xx) — silencing them hides the
// outage from Sentry. See
// `docs/architecture/resilience-to-supabase-outages.md` (Layer 5) and the
// 2026-05-24 postmortem for the regression these removals fix.
// `OUTAGE_SIGNAL_PATTERNS` above enforces this mechanically.
// Enforcement: critical-rules-supabase-outage-resilience clause.
const IGNORED_ERROR_PATTERNS = [
  // Transient user-network errors (browser-side noise, NOT outage signals)
  /load failed/i,
  /network request failed/i,
  /network error/i,
  // Client-disconnect aborts ONLY. `AbortController.abort()` — the browser
  // navigating away mid-request, or a caller signal composed in by
  // `AbortSignal.any` — rejects with an `AbortError` whose message is "This
  // operation was aborted". That is benign.
  //
  // These patterns deliberately do NOT match "The operation was aborted due
  // to timeout" (`TimeoutError`), which is Layer 6 reporting a real database
  // stall. The bare `/abort/i` this replaces could not tell the two apart and
  // dropped both (BAL-891).
  /this operation was aborted/i,
  // Node's own AbortError wording (fs, streams, `events.once`) says "The"
  // rather than "This". The negative lookahead is what keeps the Layer-6
  // timeout — "The operation was aborted DUE TO TIMEOUT" — out of this
  // pattern; `OUTAGE_SIGNAL_PATTERNS` would also catch it, so this is
  // belt-and-braces.
  /the operation was aborted(?! due to timeout)/i,
  /the user aborted a request/i,
  /request aborted/i,
  // The bare string Node's http layer uses for a client disconnect
  // (`IncomingMessage` / raw-body, ECONNRESET class). Anchored so it cannot
  // swallow a longer message that merely starts with the word.
  /^aborted$/i,
  /\baborterror\b/i,
  // Client disconnected mid-RSC-stream (BALLEE-H1 / BAL-954). Same benign
  // class as the aborts above — it only reaches us because Next 16.3.0 fails
  // to RECOGNISE it as an abort.
  //
  // React's Flight server registers a cancel handler on the destination:
  //   destination.on("close", createCancelHandler(
  //     request, "The destination stream closed early."));
  //   createCancelHandler = (request, reason) => () => {
  //     request.destination = null; abort(request, Error(reason)); };
  // (react-server-dom-turbopack-server.node.js). So `close` firing while the
  // payload is still flowing — tab closed, navigation away, cancelled
  // prefetch, crawler hanging up on a slow cold start — aborts the render.
  //
  // Next then misses it, because `next/dist/server/pipe-readable.js` matches
  // aborts by NAME only:
  //   isAbortError = (e) => e?.name === 'AbortError' || e?.name === ResponseAbortedName;
  // React throws a PLAIN Error (`name === 'Error'`), so the cancel is routed
  // to `onRequestError` as though it were a real render fault, and
  // apps/web/instrumentation.ts captures it. Upstream regression
  // vercel/next.js#96704; fix PR #96717 unmerged as of 2026-08-17. When that
  // lands and we upgrade, this pattern goes dead but stays harmless.
  //
  // Evidence (2026-08-17): 2 events, 0 users, two DIFFERENT lambdas, two
  // unrelated routes (`/`, `/auth/sign-up`), both 2-3s after a cold start,
  // zero in-app stack frames. Decisively, the Vercel runtime log shows BOTH
  // requests returned **HTTP 200** (`GET /auth/sign-up 200`, `GET / 200`),
  // with zero 5xx in either window and 221 MiB RSS of 2,288 MiB at 5.7s of
  // uptime — so this is neither a failed request nor a killed lambda. Nothing
  // is broken; the response the client abandoned is the only casualty.
  //
  // Deliberately narrow: the SIBLING handler on the same `pipe()` call,
  // "The destination stream errored while writing data.", fires on `error`
  // rather than `close` and means a genuine write failure. It stays visible.
  // Drops still emit `onIgnoredDrop` -> PostHog, so a burst of these remains
  // queryable rather than invisible (BAL-891).
  /the destination stream closed early/i,
  // Database transient errors (kept — these are app-level retry noise, not
  // outage symptoms; the outage-symptom patterns above were intentionally
  // removed per the comment block at the top of this array)
  /database retry/i,
  // Browser-specific transient errors
  /could not load "util"/i,
  /resizeobserver loop/i,
  /script error/i,
  // Expected validation errors (business logic)
  /dancer is already cast/i,
  /already assigned/i,
  /scheduling conflict/i,
  // Auth validation errors (expected user input errors)
  /new password should be different from the old password/i,
  /password is too weak/i,
  /user already registered/i,
  /email already in use/i,
  // Account deletion flow — user-input validation, not server faults
  /invalid otp/i,
  /nonce mismatch/i,
  /otp is required/i,
  /invalid form data/i,
  /account deletion is not enabled/i,
  /super admins cannot delete their own account/i,
  // Rate limiting (expected security feature)
  /rate limit/i,
  /you can only request this after/i,
  /email rate limit exceeded/i,
  // Vue.js / Vue Router noise from third-party widgets injecting compiled Vue
  // into pages we render. We are React/Next.js; these stack frames come from
  // affiliate marketing scripts and webview-injected SDKs (IAB, social ads).
  /avoided redundant navigation to current location/i,
  /cannot read properties of undefined \(reading 'styleClasses'\)/i,
  // IAB in-app browser SDK noise (Instagram/Facebook/TikTok webviews).
  /error invoking enabledidusertypeonkeyboardlogging/i,
  /java object is gone/i,
];

/**
 * Return the first matching ignore pattern's source (a stable slug for
 * telemetry), or null when the error must be kept.
 *
 * `OUTAGE_SIGNAL_PATTERNS` wins over every ignore pattern — see its docblock.
 */
function findIgnoredPattern(errorValue: string): string | null {
  if (isOutageSignal(errorValue)) {
    return null;
  }
  for (const pattern of IGNORED_ERROR_PATTERNS) {
    if (pattern.test(errorValue)) {
      return pattern.source;
    }
  }
  return null;
}

/**
 * Telemetry hook for events discarded by `IGNORED_ERROR_PATTERNS` (BAL-891).
 *
 * The server mirror of the browser hook shipped by BAL-856
 * (`sentry.client.config.ts`). BAL-856 instrumented the browser only, and the
 * bursts turned out to be server-side, so the instrumentation was blind to
 * exactly the class it was built to explain: 551 discards in three hours on
 * 2026-08-09/10, all at hours with no human traffic, none of them queryable
 * anywhere.
 *
 * Payload shape and sanitization are shared with the browser half in
 * `drop-telemetry.ts` — all three runtimes write one PostHog event, so they
 * must redact identically.
 */
export type { IgnoredDropInfo, IgnoredDropHandler };

/**
 * @name initializeSentryServerClient
 * @description Initialize the Sentry client in the server with automatic error categorization
 * @param props
 */
export function initializeSentryServerClient(
  props: Parameters<typeof init>[0] & {
    onIgnoredDrop?: IgnoredDropHandler;
    /**
     * BAL-1038: attribute drops made by an EVENT PROCESSOR in this runtime.
     * Until this existed the `event_processor` sink ran in the browser only,
     * so a server or edge share of Sentry's `client_discard / event_processor`
     * counter was unattributable by construction.
     */
    onProcessorDrop?: ProcessorDropHandler;
    /** Liveness beacon for `onProcessorDrop`. Fires once per process. */
    onSinkArmed?: SinkArmedHandler;
  } = {},
) {
  // Extract beforeSend from props to prevent it from overriding ours via spread.
  // If a caller provides a custom beforeSend, we chain it after our filters.
  const {
    beforeSend: callerBeforeSend,
    beforeBreadcrumb: callerBeforeBreadcrumb,
    onIgnoredDrop,
    onProcessorDrop,
    onSinkArmed,
    ...restProps
  } = props ?? {};

  const resolvedEnvironment = resolveEnvironment();

  const reportIgnoredDrop = createIgnoredDropReporter({
    onIgnoredDrop,
    resolvedEnvironment,
  });

  /**
   * Discard the event, attributed. The ONLY way `beforeSend` returns null.
   *
   * Dropping and attributing the drop are one statement here, so they cannot
   * come apart — which is the actual BAL-953 fix. BAL-856 and BAL-891 each
   * added a `reportIgnoredDrop` call NEXT TO one `return null` and left the
   * other `return null`s untouched; nothing failed, because a silent drop
   * looks exactly like an event that never happened. Convention had two
   * chances and lost both. `drop-reason-coverage.test.ts` fails the build on a
   * bare `return null` inside `beforeSend`.
   */
  function dropEvent(args: {
    reason: DropReason;
    errorValue: string;
    event: ErrorEvent;
    pattern?: string;
  }): null {
    reportIgnoredDrop(args);
    return null;
  }

  const client = init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Derive Sentry environment from VERCEL_ENV so preview deployments don't
    // pollute the production signal. Caller can still override via restProps.
    environment: resolvedEnvironment,

    // Performance monitoring - sample 5% of transactions (BAL-973, halved
    // from 0.1 to match BASE_TRACES_SAMPLE_RATE — see that constant's
    // docblock for the quota arithmetic). NOT a dead fallback: this is the
    // rate `SentryMonitoringService`'s server branch actually gets.
    // `getServerMonitoringService()` (packages/monitoring/api/src/services/
    // get-server-monitoring-service.ts) constructs it with only
    // `{ environment }` — no tracesSampler — and, unlike the browser branch,
    // the server branch has no existing-client guard (see
    // `browser-init-yields-to-instrumentation.test.ts`), so it calls
    // `initializeSentryServerClient` unconditionally. Reached in production
    // via `apps/web/instrumentation.ts` and `apps/web/app/api/db/webhook/
    // route.ts`. When the caller supplies a tracesSampler, no rate default is
    // unconditionally. Reached in production on the ERROR path only:
    // `apps/web/instrumentation.ts`'s `onRequestError` hook constructs the
    // service (not `register()` / `registerMonitoringInstrumentation`, which
    // runs at cold start and never calls it), and the DB webhook route's
    // catch block does the same. Not every request — every request that
    // ERRORS. When the caller supplies a tracesSampler, no rate default is
    // set: the SDK must see only one sampling mechanism on the init call.
    ...(restProps?.tracesSampler
      ? {}
      : { tracesSampleRate: restProps?.tracesSampleRate ?? 0.05 }),

    // Note: if you want to override the automatic release value, do not set a
    // `release` value here - use the environment variable `SENTRY_RELEASE`, so
    // that it will also get attached to your source maps,
    ...restProps,

    // BAL-1144 R4: breadcrumbs carry outgoing request URLs, and a Supabase
    // query names row values (`email=eq.anna@x.com`). Redact every query
    // value, keep the shape. AFTER the spread so a caller cannot disable it;
    // a caller's hook runs on the already-redacted breadcrumb.
    beforeBreadcrumb(breadcrumb, hint) {
      const redacted = redactBreadcrumbQuery(breadcrumb);
      return callerBeforeBreadcrumb
        ? callerBeforeBreadcrumb(redacted, hint)
        : redacted;
    },

    // beforeSend and beforeSendTransaction AFTER the spread so they're never overridden
    beforeSend(event, hint) {
      // Raw text for the drop branches that fire before any message matching.
      // Same fallback chain the Cloudflare-fingerprint block below uses.
      // `buildDropInfo` redacts it — nothing raw reaches the sink.
      const dropText = () =>
        resolveEventText({
          message: event.message,
          originalError: hint.originalException,
          exceptionValue: event.exception?.values?.[0]?.value,
        });

      // Drop everything from Vercel preview deployments (BAL-791). Previews
      // point at the throwaway e2e Supabase branch and report its rebuild
      // artifacts (42501 permission-denied, BALLEE-GK/GJ) as if they were
      // product defects. Deliberate tradeoff: preview-only crashes no longer
      // reach Sentry. Production/staging are untouched.
      //
      // Reported since BAL-953 — see the browser half for why this half
      // reports preview/localhost while the `event_processor` sink does not.
      if (isPreviewEnvironment(resolvedEnvironment)) {
        return dropEvent({
          reason: DROP_REASONS.previewEnvironment,
          errorValue: dropText(),
          event,
        });
      }

      // Drop events from local dev. Misconfigured local environments stamp
      // `production` on the environment tag while serving from localhost:3012,
      // which floods the production project with noise (account-not-found,
      // ZodErrors against staging schema, dev-bundler internals, etc.).
      //
      // The message text does not ship from here — that is decided in
      // `reportIgnoredDrop` by the ORIGIN, not by this branch.
      //
      // The condition PARSES the host instead of substring-matching the whole
      // URL, which is a security fix, not a cleanup (found by the BAL-953
      // security review). `.includes('://localhost')` also matched the QUERY
      // STRING, so any request to a production URL carrying
      // `?next=http://localhost:3000` had its errors silently dropped — an
      // unauthenticated caller could blind Sentry to every server error their
      // own traffic produced, just by appending a parameter. Measured:
      // `https://flow.ballee.co/home/invoices?next=http://localhost:3000`
      // was dropped; the same URL without the parameter reported.
      //
      // It compares against the SAME two hostnames as before, deliberately —
      // not the broader `isLocalHostname` set behind `isLocalOrigin`, which
      // also matches `0.0.0.0`, RFC 1918 and `.local`. Using that here would
      // WIDEN the filter and start silencing
      // events that currently reach Sentry, which is a separate decision from
      // the one this issue is about. So this change is strictly narrowing:
      // every event it stops dropping is one Sentry should have seen. The
      // dev-machine hosts it does not recognise are still covered on the
      // telemetry side by `isLocalOrigin`, which is where the PII risk lives.
      const requestHostname = resolveRequestHostname(event);
      if (requestHostname === 'localhost' || requestHostname === '127.0.0.1') {
        return dropEvent({
          reason: DROP_REASONS.localhost,
          errorValue: dropText(),
          event,
        });
      }

      const originalError = hint.originalException;
      const eventExceptionValue =
        event.exception?.values?.[0]?.value?.toLowerCase() || '';

      // Drop Next.js `notFound()` control-flow signals. Their digest
      // (NEXT_HTTP_ERROR_FALLBACK;404 / NEXT_NOT_FOUND) leaks to Sentry when
      // notFound() fires inside a POST server-action re-render, past the SDK's
      // own notFound filter. These are expected 404s, not crashes.
      // See BALLEE-GF / BALLEE-GG.
      if (isNextNotFoundError(originalError)) {
        return dropEvent({
          reason: DROP_REASONS.nextNotFound,
          errorValue: dropText(),
          event,
        });
      }

      // Cloudflare-origin-timeout fingerprint. When Supabase Postgres is
      // unreachable from Vercel, REST calls return an HTML 5xx body from
      // Cloudflare (522/523/524) instead of JSON. Each unique body becomes
      // a "unique" Sentry issue because the message includes random request
      // IDs — without this fingerprint, a single outage produces hundreds
      // of unrelated-looking issues that swamp triage. Group them all
      // under one canonical fingerprint, tag the upstream class, and
      // truncate the HTML body so log search isn't poisoned.
      // See `docs/architecture/resilience-to-supabase-outages.md` § Layer 5.
      const rawMsg = String(
        event.message ??
          (originalError instanceof Error
            ? originalError.message
            : typeof originalError === 'string'
              ? originalError
              : (event.exception?.values?.[0]?.value ?? '')),
      );
      if (
        rawMsg.includes('<!DOCTYPE html>') ||
        /\b52[234]\b/.test(rawMsg) ||
        rawMsg.startsWith('<html')
      ) {
        event.fingerprint = ['cloudflare-origin-timeout'];
        event.tags = {
          ...event.tags,
          upstream_class: 'cloudflare-522',
          outage_signal: 'true',
        };
        event.level = 'error';
        if (event.message && event.message.length > 200) {
          event.message =
            event.message.slice(0, 200) + '... [HTML body truncated]';
        }
        const exceptionValue = event.exception?.values?.[0]?.value;
        if (exceptionValue && exceptionValue.length > 200) {
          event.exception!.values![0]!.value =
            exceptionValue.slice(0, 200) + '... [HTML body truncated]';
        }
        return event;
      }

      // Drop "missing NEXT_PUBLIC_SUPABASE_*" ZodErrors from non-production
      // environments. Vercel preview deploys without these env vars crash
      // every page render — capturing one signal is fine, capturing 466
      // events in 1.5h pollutes the prod signal. See BAL-169.
      if (
        resolvedEnvironment !== 'production' &&
        isMissingSupabaseKeysError(originalError, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.missingSupabaseKeys,
          errorValue: dropText(),
          event,
        });
      }

      // Drop the getter TypeError ("Cannot set property message of
      // [object Object] which has only a getter") that the pre-9d29f582f4
      // catch block in getSupabaseClientKeys threw (see
      // isPreviewSupabaseKeysTypeError). Same root cause as BAL-169 but
      // surfaces as a different Sentry issue (BAL-171 / BALLEE-BV)
      // because the type and message differ.
      if (
        resolvedEnvironment !== 'production' &&
        isPreviewSupabaseKeysTypeError(event, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.supabaseKeysTypeError,
          errorValue: dropText(),
          event,
        });
      }

      // Check for expected validation errors marked by code
      if (
        originalError &&
        typeof originalError === 'object' &&
        'isExpectedValidation' in originalError &&
        (originalError as { isExpectedValidation?: boolean })
          .isExpectedValidation
      ) {
        // Don't report expected validation errors to Sentry — but DO record
        // the drop, so the discard counter stays reconcilable (BAL-953).
        return dropEvent({
          reason: DROP_REASONS.expectedValidation,
          errorValue: dropText(),
          event,
        });
      }

      // Drop the expected anonymous-caller auth miss. The auth wrappers
      // (withAuth / withAuthParams) already capture it once at `warning`
      // level via captureAuthorizationError, then re-throw the sentinel so
      // the caller / error boundary observe identical behaviour. When that
      // sentinel escapes the server action, Next.js's outer onRequestError
      // handler re-captures it here — and the auth auto-categorization below
      // escalates any `*Auth*` error to `fatal`. That framework-level
      // duplicate is the BALLEE-D7 regression (4th surface): commit
      // d42e83fbf6 stopped the wrapper's own double-capture but not this one.
      // The throw site marks the error with `isExpectedAuthMiss = true` (see
      // NotAuthenticatedError in apps/web/lib/auth-wrappers.ts).
      if (
        originalError &&
        typeof originalError === 'object' &&
        'isExpectedAuthMiss' in originalError &&
        (originalError as { isExpectedAuthMiss?: boolean }).isExpectedAuthMiss
      ) {
        return dropEvent({
          reason: DROP_REASONS.expectedAuthMiss,
          errorValue: dropText(),
          event,
        });
      }

      // Drop errors that were already captured at the service boundary with
      // richer context (tags, error code, step). Without this, the outer
      // Next.js onRequestError handler captures a second, less-tagged event
      // for the same failure — see BALLEE-CD / BALLEE-CE duplicate. The
      // throwing site marks the error with `alreadyCapturedToSentry = true`
      // (see captureServiceError() / @kit/shared/lib/sentry-helpers).
      if (
        originalError &&
        typeof originalError === 'object' &&
        'alreadyCapturedToSentry' in originalError &&
        (originalError as { alreadyCapturedToSentry?: boolean })
          .alreadyCapturedToSentry
      ) {
        return dropEvent({
          reason: DROP_REASONS.alreadyCaptured,
          errorValue: dropText(),
          event,
        });
      }

      // Filter out transient/ignored errors
      const errorMessage =
        originalError instanceof Error
          ? originalError.message
          : typeof originalError === 'string'
            ? originalError
            : '';

      if (errorMessage) {
        const matched = findIgnoredPattern(errorMessage);
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            errorValue: errorMessage,
            event,
          });
        }
      }

      // Also check event exception values
      if (eventExceptionValue) {
        const matched = findIgnoredPattern(eventExceptionValue);
        if (matched) {
          // Report the ORIGINAL-case value, not the lowercased copy used for
          // matching. Lowercasing fragments grouping in PostHog against the
          // other call site, and it silently defeats any case-sensitive
          // redaction rule.
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            errorValue:
              event.exception?.values?.[0]?.value ?? eventExceptionValue,
            event,
          });
        }
      }

      // Ensure we capture the full error details
      if (hint.originalException) {
        // Add the full error to extras for complete visibility
        event.extra = {
          ...event.extra,
          originalError:
            hint.originalException instanceof Error
              ? {
                  message: hint.originalException.message,
                  stack: hint.originalException.stack,
                  name: hint.originalException.name,
                }
              : hint.originalException,
        };

        // For server component errors, capture the full message
        if (
          hint.originalException instanceof Error &&
          hint.originalException.message.includes('digest')
        ) {
          event.contexts = {
            ...event.contexts,
            'Next.js Error': {
              digestError: true,
              fullMessage: hint.originalException.message,
            },
          };
        }
      }

      // Automatic error categorization and tagging
      if (event.exception?.values?.[0]) {
        const error = event.exception.values[0];
        const errorType = error.type || '';
        const errorValue = error.value?.toLowerCase() || '';

        // Initialize tags if not present
        event.tags = event.tags || {};

        // Auto-categorize authentication errors
        if (
          errorType.includes('Auth') ||
          errorValue.includes('unauthorized') ||
          errorValue.includes('forbidden') ||
          errorValue.includes('permission')
        ) {
          event.tags.errorCategory = 'authentication';
          event.level = 'fatal';
          event.tags.priority = 'critical';
        }

        // Auto-categorize database errors
        if (
          errorType.includes('Database') ||
          errorValue.includes('database') ||
          errorValue.includes('sql') ||
          errorValue.includes('postgres') ||
          errorValue.includes('supabase')
        ) {
          event.tags.errorCategory = 'database';
          event.level = 'fatal';
          event.tags.priority = 'critical';
        }

        // Auto-categorize payment errors
        if (
          errorType.includes('Payment') ||
          errorValue.includes('payment') ||
          errorValue.includes('billing') ||
          errorValue.includes('stripe') ||
          errorValue.includes('transaction')
        ) {
          event.tags.errorCategory = 'payment';
          event.level = 'fatal';
          event.tags.priority = 'critical';
        }

        // Auto-categorize validation errors
        if (
          errorType.includes('Validation') ||
          errorType.includes('Zod') ||
          errorValue.includes('validation') ||
          errorValue.includes('invalid')
        ) {
          event.tags.errorCategory = 'validation';
          event.level = 'error';
          event.tags.priority = 'medium';
        }

        // Auto-categorize not found errors
        if (
          errorValue.includes('not found') ||
          errorValue.includes('404') ||
          errorType.includes('NotFound')
        ) {
          event.tags.errorCategory = 'not_found';
          event.level = 'warning';
          event.tags.priority = 'low';
        }

        // Auto-categorize timeout errors
        if (
          errorValue.includes('timeout') ||
          errorValue.includes('timed out') ||
          errorType.includes('Timeout')
        ) {
          event.tags.errorCategory = 'timeout';
          event.level = 'warning';
          event.tags.priority = 'medium';
        }

        // Auto-categorize network errors
        if (
          errorValue.includes('network') ||
          errorValue.includes('fetch') ||
          errorValue.includes('connection')
        ) {
          event.tags.errorCategory = 'network';
          event.level = 'error';
          event.tags.priority = 'medium';
        }

        // Mark outage signals so they are alertable (BAL-891). Without this,
        // a Layer-6 fetch-budget blow lands as `warning` / `priority: medium`
        // via the timeout categorizer above — below the severity of the
        // outage it actually represents, and invisible to any alert rule
        // scoped to error/fatal. Mirrors the `outage_signal` tag the
        // Cloudflare-522 fingerprint block already sets, which is what
        // `sentry-alert-to-linear.yml` filters on.
        if (isOutageSignal(errorValue)) {
          event.tags.outage_signal = 'true';
          event.tags.errorCategory = 'database';
          event.tags.priority = 'critical';
          event.level = 'error';
        }

        // Add environment-specific tags. Mirror the resolved Sentry
        // environment (VERCEL_ENV-aware) so the tag matches the top-level
        // event environment instead of NODE_ENV (which is always
        // "production" on Vercel previews).
        event.tags.environment = resolvedEnvironment;
        event.tags.runtime = 'nodejs';
      }

      // Chain caller's beforeSend if provided
      if (callerBeforeSend) {
        return callerBeforeSend(event, hint);
      }

      return event;
    },

    // Performance monitoring configuration
    beforeSendTransaction(transaction) {
      // Add custom tags to transactions for better filtering
      if (transaction.tags) {
        transaction.tags.environment = resolvedEnvironment;
        transaction.tags.runtime = 'nodejs';
      }

      return transaction;
    },
  });

  // BAL-1038: the server and edge half of BAL-959's `event_processor` sink.
  // Same instrument as the browser, installed after `init` for the same
  // reason: the integrations' processors are registered during `init`.
  //
  // The scope half matters MORE here than in the browser. @sentry/nextjs
  // registers `DropReactControlFlowErrors` on the GLOBAL scope in both server
  // runtimes, so a client-only wrap would miss the one server processor that
  // drops by design.
  //
  // `init` returns the client on Node, and ALWAYS returns `undefined` on Edge:
  // the @sentry/nextjs edge `init` has no `return client` at all. So on Edge
  // `getClient()` is the normal path, not a fallback for a double bind.
  // Without it the Edge install would report `not-wired` on every boot and
  // leave that runtime as blind as before.
  if (onProcessorDrop) {
    instrumentEventProcessorDrops({
      client: client ?? getClient(),
      environment: resolvedEnvironment,
      onProcessorDrop,
      onSinkArmed,
      scopes: [
        { kind: 'global', resolve: getGlobalScope },
        { kind: 'isolation', resolve: getIsolationScope },
        { kind: 'current', resolve: getCurrentScope },
      ],
      // BAL-1038: the same 60s window as the `beforeSend` throttle. The browser
      // sink budgets per pageload, and a server process has no pageload.
      budgetWindowMs: DROP_REPORT_WINDOW_MS,
    });
  }

  return client;
}
